import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
  CardHeader,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, mediaUrl, uploadFile } from "../../../Utils/FileUpload";
import { Submit, AddAgentTitle, AgentTitle } from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import useCustomToasts from "../../../hooks/useCustomToast";
import { MultiSelect } from "react-multi-select-component";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
interface FormValues {
  agent_name: string;
  phone: string;
  email: string;
  address: string;
  image: FileList;
  language_id: string[]; // changed to array
  brand_id: string[]; // changed to array
  password: string;
  documents: {
    name: string;
    image: FileList | null;
  }[];
  bankingdetail?: string;
}

interface ErrorResponse {
  message: string;
}

interface Language {
  id: string;
  language: string;
  icon: string;
}

interface Brand {
  id: string;
  brand_name: string;
  icon: string;
}

export default function AddAgent() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      documents: [{ name: "", image: null }],
      language_id: [],
      brand_id: [],
    },
  });

  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState<Language[]>([]);
  const [brand, setBrand] = useState<Brand[]>([]);
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [phoneInputValue, setPhoneInputValue] = useState<string>("");
  const [documentPreviews, setDocumentPreviews] = useState<{
    [key: string]: { src: string; type: string };
  }>({});
  // for documents list
  const { fields, append, remove } = useFieldArray({
    control,
    name: "documents",
  });

  const validatePhoneNumber = (value: string) => {
    if (!value) {
      return "Phone number is required.";
    }

    const sanitizedValue = value.replace(/\D/g, ""); // remove non-digits
    const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return "Invalid phone number.";
    }

    const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
    if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
      return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
    }

    return true;
  };
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${baseUrl}language/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const filteredLanguage = response.data.filter(
          (brand: any) => brand.is_deleted === 0 && brand.is_active === 1
        );
        setLanguage(filteredLanguage);
        // console.log(data);
        const result = await axios.get(`${baseUrl}brand/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const filteredBrand = result.data.filter(
          (brand: any) => brand.is_deleted === 0 && brand.is_active === 1
        );
        setBrand(filteredBrand);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      // 1) upload main image
      const mainFile = data.image?.[0];
      if (!mainFile) {
        toast.error("Please upload a profile image.");
        return;
      }
      const mainImageName = await uploadFile(mainFile);

      // 2) upload each document image, collecting names
      const documentsPayload: { name: string; image: string }[] = [];
      for (const doc of data.documents) {
        if (!doc.name) {
          toast.error("Each document needs a name.");
          return;
        }
        const file = doc.image?.[0];
        if (!file) {
          toast.error(`Please upload an image for "${doc.name}".`);
          return;
        }
        const uploadedName = await uploadFile(file);
        documentsPayload.push({
          name: doc.name,
          image: uploadedName,
        });
      }

      // 3) build your JSON payload
      const payload = {
        agent_name: data.agent_name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        image: mainImageName,
        password: data.password,
        language_id: data.language_id,
        brand_id: data.brand_id,
        documents: documentsPayload,
        bankingdetail: data.bankingdetail,
      };
      const token = localStorage.getItem("token");
      const response = await axios.post(`${baseUrl}agent/store`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200 || response.status === 201) {
        SuccessToast("Agent added successfully", "/view_agent");
      } else {
        ErrorToast("Failed to add agent");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";
      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => setIsButtonDisabled(false), 2000);
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        pageTitle={AddAgentTitle}
        parent={AgentTitle}
        title={AddAgentTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={6}>
                  <Label htmlFor="agent-name">Agent Name</Label>
                  <Controller
                    name="agent_name"
                    control={control}
                    rules={{
                      required: "Agent is required",
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="agent-name"
                          type="text"
                          placeholder="Agent Name"
                          {...field}
                          invalid={!!errors.agent_name}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.agent_name ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.agent_name && (
                          <FormFeedback>
                            {errors.agent_name.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="phone">Phone</Label>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      required: "Phone number is required",
                      validate: (value) => validatePhoneNumber(value),
                    }}
                    render={({ field }) => (
                      <>
                        <PhoneInput
                          country={"in"} // default country
                          onlyCountries={["in", "sa", "ae"]} // restrict to India, KSA, UAE
                          disableDropdown={false}
                          countryCodeEditable={false}
                          value={phoneInputValue}
                          onChange={(value) => {
                            setPhoneInputValue(value);
                            field.onChange(value);
                          }}
                          inputProps={{
                            id: "phone",
                            placeholder: "Enter phone number",
                            style: {
                              backgroundColor: "var(--input-background-color)",
                              color: "var(--input-text-color)",
                              width: "100%",
                              borderColor: errors.phone
                                ? "red"
                                : "var(--input-border-color, #e0e0e0)",
                            },
                            autoComplete: "off",
                          }}
                          dropdownStyle={{
                            backgroundColor: "var(--input-background-color)",
                            backdropFilter: "blur(1000px)",
                          }}
                          buttonStyle={{
                            backgroundColor: "var(--input-background-color)",
                          }}
                          specialLabel=""
                          onKeyDown={handleKeyDown}
                        />
                        {errors.phone && (
                          <FormFeedback
                            style={{ display: "block", color: "red" }}
                          >
                            {errors.phone.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="email">Email</Label>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/,
                        message:
                          "Invalid email address. Please enter a valid email.",
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="email"
                          id="email"
                          placeholder="Email"
                          {...field}
                          invalid={!!errors.email}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.email ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.email && (
                          <FormFeedback>{errors.email.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="address">Address</Label>
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: "Address is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="textarea"
                          id="address"
                          placeholder="Address"
                          {...field}
                          invalid={!!errors.address}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.address ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                          autoComplete="off"
                        />
                        {errors.address && (
                          <FormFeedback>{errors.address.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="brand_id">Brand</Label>
                  <Controller
                    name="brand_id"
                    control={control}
                    rules={{ required: "Please select at least one brand" }}
                    render={({ field }) => {
                      const options = brand.map((b: any) => ({
                        label: b.brand_name,
                        value: b.id,
                      }));

                      return (
                        <>
                          <div
                            style={{
                              border: errors.brand_id
                                ? "1px solid red"
                                : "1px solid var(--input-border-color, #ccc)",
                              borderRadius: "4px",
                            }}
                          >
                            <MultiSelect
                              options={options}
                              value={options.filter((opt) =>
                                field.value?.includes(opt.value)
                              )}
                              onChange={(
                                selected: {
                                  label: string;
                                  value: string | number;
                                }[]
                              ) =>
                                field.onChange(selected.map((opt) => opt.value))
                              }
                              labelledBy="Select Brands"
                              hasSelectAll={false}
                              overrideStrings={{
                                selectSomeItems: "Select Brand(s)...",
                              }}
                            />
                          </div>
                          {errors.brand_id && (
                            <div className="text-danger mt-1 d-block">
                              {errors.brand_id.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="language_id">Language</Label>
                  <Controller
                    name="language_id"
                    control={control}
                    rules={{ required: "Please select at least one language" }}
                    render={({ field }) => {
                      const options = language.map((b: any) => ({
                        label: b.language,
                        value: b.id,
                      }));

                      return (
                        <>
                          <div
                            style={{
                              border: errors.language_id
                                ? "1px solid red"
                                : "1px solid var(--input-border-color, #ccc)",
                              borderRadius: "4px",
                            }}
                          >
                            <MultiSelect
                              options={options}
                              value={options.filter((opt) =>
                                field.value?.includes(opt.value)
                              )}
                              onChange={(
                                selected: {
                                  label: string;
                                  value: string | number;
                                }[]
                              ) =>
                                field.onChange(selected.map((opt) => opt.value))
                              }
                              labelledBy="Select Languages"
                              hasSelectAll={false}
                              overrideStrings={{
                                selectSomeItems: "Select Language(s)...",
                              }}
                            />
                          </div>
                          {errors.language_id && (
                            <div className="text-danger mt-1">
                              {errors.language_id.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </Col>

                <Col xs={12}>
                  <Card
                    className="mb-4"
                    style={{ border: "1px solid #ced4da", borderRadius: "8px" }}
                  >
                    <CardHeader
                      tag="h5"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #ced4da",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      Documents Details
                    </CardHeader>
                    <CardBody>
                      {fields.map((doc, index) => (
                        <Row key={doc.id} className="mb-2">
                          <Col xs={6}>
                            <Label>Document Name</Label>
                            <Controller
                              name={`documents.${index}.name`}
                              control={control}
                              rules={{ required: "Document name required" }}
                              render={({ field }) => (
                                <Input
                                  placeholder="Document Name"
                                  {...field}
                                  invalid={!!errors.documents?.[index]?.name}
                                  style={{
                                    color: "var(--input-text-color)",
                                    borderColor: errors.documents?.[index]?.name
                                      ? "red"
                                      : "",
                                  }}
                                  autoComplete="off"
                                  onKeyDown={handleKeyDown}
                                />
                              )}
                            />
                            {errors.documents?.[index]?.name && (
                              <FormFeedback>
                                {errors.documents[index]?.name?.message}
                              </FormFeedback>
                            )}
                          </Col>

                          <Col xs={6}>
                            <Label>Upload Document</Label>
                            <Controller
                              name={`documents.${index}.image`}
                              control={control}
                              rules={{ required: "Image file is required" }}
                              render={({
                                field: { onChange, onBlur, ref },
                              }) => (
                                <>
                                  <Input
                                    type="file"
                                    accept="image/*,.pdf"
                                    placeholder="Select file"
                                    onChange={(e) => {
                                      const files = e.target.files;
                                      if (files && files[0]) {
                                        const file = files[0];
                                        const fileType = file.type;

                                        if (fileType.startsWith("image/")) {
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                            //@ts-ignore
                                            setDocumentPreviews((prev) => ({
                                              ...prev,
                                              [doc.id]: {
                                                type: "image",
                                                src: reader.result as string,
                                              },
                                            }));
                                          };
                                          reader.readAsDataURL(file);
                                        } else if (
                                          fileType === "application/pdf"
                                        ) {
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                             //@ts-ignore
                                            setDocumentPreviews((prev) => ({
                                              ...prev,
                                              [doc.id]: {
                                                type: "pdf",
                                                src: reader.result as string,
                                              },
                                            }));
                                          };
                                          reader.readAsDataURL(file);
                                        }

                                        onChange(files);
                                      }
                                    }}
                                    onBlur={onBlur}
                                    innerRef={ref}
                                    invalid={!!errors.documents?.[index]?.image}
                                    style={{
                                      borderColor: errors.documents?.[index]
                                        ?.image
                                        ? "red"
                                        : "",
                                    }}
                                  />

                                  {!!errors.documents?.[index]?.image && (
                                    <div className="invalid-feedback d-block">
                                      {errors.documents[index]?.image?.message}
                                    </div>
                                  )}

                                  {/* Show Preview */}
                                  {documentPreviews[doc.id]?.type ===
                                    "image" && (
                                    <img
                                      src={documentPreviews[doc.id].src}
                                      alt="Document Preview"
                                      width="70"
                                      height="70"
                                      style={{
                                        marginTop: "5px",
                                        borderRadius: "10px",
                                      }}
                                    />
                                  )}
                                  {documentPreviews[doc.id]?.type === "pdf" && (
                                    <iframe
                                      src={documentPreviews[doc.id].src}
                                      // type="application/pdf"
                                      width="100%"
                                      height="100px"
                                      allow="fullscreen"
                                      style={{
                                        marginTop: "5px",
                                        borderRadius: "10px",
                                      }}
                                    />
                                    
                                  )}
                                </>
                              )}
                            />
                          </Col>

                          <Col xs={2}>
                            {fields.length > 1 && index !== 0 && (
                              <Btn
                                color="danger"
                                size="sm"
                                onClick={() => {
                                  setDocumentPreviews((prev) => {
                                    const updated = { ...prev };
                                    delete updated[doc.id];
                                    return updated;
                                  });
                                  remove(index);
                                }}
                              >
                                Remove
                              </Btn>
                            )}
                          </Col>
                        </Row>
                      ))}

                      <Btn
                        size="sm"
                        onClick={() => append({ name: "", image: null })}
                      >
                        + Add Document
                      </Btn>
                    </CardBody>
                  </Card>
                </Col>

                <Col xs={12}>
                  <Card
                    className="mb-4"
                    style={{ border: "1px solid #ced4da", borderRadius: "8px" }}
                  >
                    <CardHeader
                      tag="h5"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #ced4da",
                        borderTopLeftRadius: "10px",
                        borderTopRightRadius: "10px",
                      }}
                    >
                      Banking Details 
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col xs={12} className="mb-2">
                          <Controller
                            name="bankingdetail"
                            control={control}
                            rules={{ required: "Banking details are required" }}
                            render={({ field }) => (
                              <>
                                <Input
                                  type="textarea"
                                  id="bankingdetail"
                                  placeholder="Enter all banking details here"
                                  {...field}
                                  invalid={!!errors.bankingdetail}
                                  style={{
                                    color: "var(--input-text-color)",
                                    borderColor: errors.bankingdetail
                                      ? "red"
                                      : "",
                                    minHeight: "120px",
                                  }}
                                  autoComplete="off"
                                  onKeyDown={handleKeyDown}
                                />
                                {errors.bankingdetail && (
                                  <FormFeedback>
                                    {errors.bankingdetail.message}
                                  </FormFeedback>
                                )}
                              </>
                            )}
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>

                <Col xs={6}>
                  <Label htmlFor="formFile">Photo</Label>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: "Image is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              onChange(e.target.files);
                              // Update the image preview immediately
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setExistingFile(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          onBlur={onBlur}
                          innerRef={ref}
                          invalid={!!errors.image}
                          style={{ borderColor: errors.image ? "red" : "" }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.image && (
                          <FormFeedback>{errors.image.message}</FormFeedback>
                        )}
                        {existingFile && (
                          <img
                            src={
                              existingFile.startsWith("data:")
                                ? existingFile
                                : `${mediaUrl}/${existingFile}`
                            }
                            alt="Preview"
                            width="70"
                            height="70"
                            style={{ marginTop: "5px", borderRadius: "10px" }}
                          />
                        )}
                      </>
                    )}
                  />
                </Col>

                {/* Password */}
                <Col xs={6}>
                  <Label htmlFor="password">
                    Password 
                  </Label>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                      pattern: {
                        value: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
                        message: "Password must contain letters and numbers",
                      },
                    }}
                    render={({ field }) => (
                      <div style={{ position: "relative" }}>
                        <Input
                          type={isPasswordVisible ? "text" : "password"}
                          id="password"
                          placeholder="Enter password"
                          {...field}
                          invalid={!!errors.password}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.password ? "red" : "",
                          }}
                          autoComplete="new-password"
                        />
                        <div
                          onClick={togglePasswordVisibility}
                          style={{
                            position: "absolute",
                            right:  errors.password ? "30px" : "10px",
                            top: errors.password ? "35%" : "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            color: "#888",
                          }}
                        >
                          {isPasswordVisible ? "Hide" : "Show"}
                        </div>
                        {errors.password && (
                          <FormFeedback>{errors.password.message}</FormFeedback>
                        )}
                      </div>
                    )}
                  />
                </Col>
                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
